import React from 'react';
import { CardRow, Button, Icon, Numbers, ButtonDropdown, DropdownItem, Dropdown, Status, InfoText, Tooltip, Chip } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import Style from './crossSelling.module.css';
import { useHistory } from 'react-router-dom';
import { getProductsLanguages, setTranslate } from '../list/translationUtils';

function ProductStatus({ product, onEdit, onClick, disabled = false }) {
    const { t } = useTranslation();

    const editStatus = React.useCallback(
        value => {
            onEdit({ ...product.original_object, disabled: !value }, product.id);
        },
        [product, onEdit]
    );

    return (
        <Tooltip triggerOnHover={disabled} visible={disabled} title={t('ticket.cross-selling.visibility_tooltip')}>
            <div className={Style[`cross-selling-line-action-status-dropdown-container`]} onClick={onClick}>
                <Status.Dropdown
                    disabled={disabled}
                    className={Style['cross-selling-line-action-status-dropdown']}
                    items={[
                        product.disabled && <DropdownItem key={1} item={t('ticket.cross-selling.visible')} onClick={() => editStatus(product.disabled)} />,
                        !product.disabled && <DropdownItem key={2} item={t('ticket.cross-selling.invisible')} onClick={() => editStatus(product.disabled)} />
                    ]}
                    color={product.disabled ? 'orange' : 'green'}
                    value={product.disabled ? 'invisible' : 'visible'}
                    text={product.disabled ? t('ticket.cross-selling.invisible') : t('ticket.cross-selling.visible')}
                />
            </div>
        </Tooltip>
    );
}
export default function CrossSellingItem({ organization, product, products, onEdit, onOpenDeleteModal, onOpenDuplicateModal, index, onSelectProduct }) {
    const { t } = useTranslation();
    const locale = i18n.language;

    const history = useHistory();

    let selectCategory = key => {
        switch (key) {
            case 1:
                return 'Cashless';
            case 2:
                return 'Product';
            case 3:
                return 'Service';
            default:
                break;
        }
    };

    let selectIcon = key => {
        switch (key) {
            case 1:
                return 'shirt';
            case 2:
                return 'service';
            case 3:
                return 'credit-card';
            default:
                break;
        }
    };

    const productLanguages = React.useMemo(() => getProductsLanguages(organization), [organization]);

    const productsNamesTranslations = React.useMemo(() => {
        const lang = i18n.accountLocale.toLowerCase().replace('_', '-');
        //for each product we either use the translations if present or the name (pipetranslate)
        return products.map(v => {
            const _translations = v.translations?.name;
            if (_translations) {
                if (_translations[lang]) return _translations[lang];
                //we tolerate alternative translations similar to account language (like fr-fr for fr-ca)
                for (const trylang of Object.keys(_translations)) {
                    if (trylang.includes(lang.split('-')[0])) return _translations[trylang];
                }
            }
            return setTranslate(v.name, lang, productLanguages);
        });
    }, [products, i18n, setTranslate, productLanguages]);

    const productCanBeVisible = React.useMemo(() => {
        return Boolean(product.variants.length && product.variants.some(vr => !vr.disabled));
    }, [product]);

    return (
        <CardRow className={Style['card-row-cross-selling-content']} as="a" href={`/ticket/O${organization.id}/cross-selling/edit/${product.id}`}>
            <CardRow.LeftComponent className={Style['card-row-left-cross-selling']}>
                <div className={Style['cross-selling-line-banner-container']}>
                    <Icon inverted name={selectIcon(product.category)} />
                </div>
            </CardRow.LeftComponent>
            <CardRow.RightComponent>
                <>
                    <div className={Style['cross-selling-line-title']}>{productsNamesTranslations[index]}</div>
                    <div className={Style['cross-selling-line-container']}>
                        <div className={Style['cross-selling-line-infos']}>
                            <div className={Style['cross-selling-line-buyer']}>
                                {product.for_buyer ? t('ticket.cross-selling.for_buyer') : t('ticket.cross-selling.for_attendee')}
                            </div>
                            <div className={Style['cross-selling-line-variant']}>{t('ticket.cross-selling.variant', { count: product.variants_total_count })}</div>
                            <div className={Style['cross-selling-line-type']}>
                                <Chip primary text={selectCategory(product.category)} />
                            </div>
                        </div>

                        <div className={Style['cross-selling-line-action-container']}>
                            <InfoText subtitle={t('ticket.cross-selling.ventes', { count: product.transaction_total_count || 0 })}>
                                <span className={Style['total']}>
                                    <Numbers value={product.transaction_total_count || 0} locale={locale} />
                                </span>
                            </InfoText>
                            <InfoText subtitle={t('ticket.cross-selling.likedEvent', { count: product.related_event_count || 0 })}>
                                <span className={Style['total']}>
                                    <Numbers value={product.related_event_count || 0} locale={locale} />
                                </span>
                            </InfoText>
                            <ProductStatus disabled={!productCanBeVisible} onClick={e => e.preventDefault()} product={product} onEdit={onEdit} />
                            <div className={Style['cross-selling-icon-button']}>
                                <Tooltip title={t('ticket.cross-selling.seeVariants')} mini={false} triggerOnHover={true} visible={true}>
                                    <CardRow.IconButton
                                        as="a"
                                        href={`/ticket/O${organization.id}/cross-selling/edit/${product.id}/page/1`}
                                        icon={<Icon name="list" margin={false} />}
                                    />
                                </Tooltip>
                            </div>
                            <ButtonDropdown
                                onClick={e => e.preventDefault()}
                                trigger={<Button primary inverted icon={<Icon size="regular" name="chevron-bottom" />} label={t('ticket.common.cta.actions')} />}
                                items={[
                                    <div key="items" className={Style['dropdown-button-items']} onClick={e => e.preventDefault()}>
                                        <DropdownItem
                                            onClick={() => {
                                                history.push(`/ticket/O${organization.id}/cross-selling/edit/${product.id}`);
                                            }}
                                            item={t('ticket.common.action.edit')}
                                        />
                                        <DropdownItem
                                            onClick={() => {
                                                onSelectProduct(product);
                                                onOpenDuplicateModal(true);
                                            }}
                                            item={t('ticket.common.action.duplicate')}
                                        />
                                        <Dropdown.Line />
                                        <Dropdown.Item
                                            disabled={product.transaction_total_count > 0}
                                            deleteItem
                                            onClick={() => {
                                                onSelectProduct(product);
                                                onOpenDeleteModal(true);
                                            }}
                                            item={t('ticket.common.action.delete')}
                                        />
                                    </div>
                                ]}
                            />
                        </div>
                    </div>
                </>
            </CardRow.RightComponent>
        </CardRow>
    );
}
